import React, {useEffect, useRef, useState} from 'react';
import { withCookies, Cookies } from 'react-cookie';
import { CookieSetOptions } from 'universal-cookie';
import LoadingScreen from './LoadingScreen/LoadingScreen';
import WelcomeChat from './WelcomeChat/WelcomeChat';
import I18nService from './services/I18nService';
import { useTranslation } from 'react-i18next';
import RainbowSDKService from './services/RainbowSDKService';
import UniversalLivechatService from './services/UniversalLivechatService';
import Config from './config/config.json';
import "./App.css";
import {Bubble} from "rainbow-web-sdk";

I18nService.init();

const States = {
  Initialising: 'Initialising',
  Initialised: 'Initialised',
  WaitOnUser: 'WaitOnUser',
  CreatingSession: 'CreatingSession',
  FillingInformationForm: 'FillingInformationForm',
  Authenticating: 'Authenticating',
  Authenticated: 'Authenticated',
  ChatArchived: 'ChatArchived',
  Expired: 'Expired',
  OutOfBusinessHours: 'OutOfBusinessHours',
  Unavailability: 'Unavailability',
  Error: 'Error',
  BadToken: 'BadToken'
} as const;

type AppState = keyof typeof States;
type ReasonForChat = {
  name: string;
  isBot?: boolean;
};

type Props = {
  cookies: Cookies;
};

type Session = {
  bubble?: Bubble;
  guest_jid?: string;
  token?: string;
};

const App: React.FC<Props> = ({ cookies }) => {
  const [appState, setAppStateState] = useState<AppState>(States.Initialising);
  const [reasonForChat, setReasonForChat] = useState<ReasonForChat | null>(null);
  const bubble = useRef<Bubble | undefined>(cookies.get("livechat_bubble") || {});
  const [bubbleId, setBubbleId] = useState<string | undefined>("");
  const { t } = useTranslation();

  const setAppState = (state: AppState) => {
    if (state === States.Error) {
      cookies.remove("livechat_bubble");
      bubble.current = undefined;
    }
    console.log("New State : ", state);
    setAppStateState(state);
  };

  const handleReasonForChatChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const reasonName = event.target.value;
    const reasonConfig = Config.chatService.reasonForChats.find((r: ReasonForChat) => r.name === reasonName);
    if (!reasonConfig) {
      console.log("[App] unknown reason for chat");
      setAppState(States.Error);
      return;
    }
    setReasonForChat(reasonConfig);
  };

  const sessionVisibilityChange = () => {
    if (bubble.current && Object.keys(bubble.current).length > 0) {
      navigator.sendBeacon(
          `${Config.chatService.url}/session-visibility-change?roomId=${bubbleId}&status=${document.visibilityState}&token=${UniversalLivechatService.getToken()}`
      );
    }
  };

  const initiateChatSession = async (language: string, reason: ReasonForChat) => {
    try {
      const urlToken = UniversalLivechatService.getToken();
      if (!urlToken) {
        console.log("[App] No token found");
        setAppState(States.BadToken);
        return;
      }

      let session : Session = {};
      let changedReason = true;
      let connectedUser: any = {};

      if (!bubble || !bubble.current || Object.keys(bubble.current).length === 0) {
        changedReason = false;
        setAppState(States.CreatingSession);

        if (!reason.isBot) {
          const response = await UniversalLivechatService.getBusinessHours(language);
          if (!response.inBusinessHours) {
            setAppState(States.OutOfBusinessHours);
            return;
          }
          await UniversalLivechatService.checkAvailability(language, reason.name);
        }

        let sessionResponse = await UniversalLivechatService.createSession(language, reason.name);
        console.log("[App] Successfully got session");
        const account = await RainbowSDKService.loginWithToken(sessionResponse.token);
        console.log("[App] Account : ", account);

        session.bubble = sessionResponse.room;
        session.guest_jid = sessionResponse.guest_jid;
      }
      else {
        connectedUser = RainbowSDKService.getConnectedUser();
        if (!connectedUser) {
          setAppState(States.Error);
          return;
        }
        session = {
          bubble: bubble.current,
          guest_jid: connectedUser.jid,
        };
      }

      if (typeof session.guest_jid !== "string") {
        setAppState(States.Error);
        return;
      }

      const joinedSession = await UniversalLivechatService.joinSession(
          session.bubble,
          session.guest_jid,
          { language: language, reasonForChat: reason.name },
          changedReason
      );

      const sessionCookie: CookieSetOptions = {
        path: "/",
        maxAge: joinedSession.ttl,
          sameSite: import.meta.env.DEV ? 'lax' : 'none',
          ...(import.meta.env.DEV ? {} : { secure: true }),
      };

      cookies.set("livechat_bubble", session.bubble, sessionCookie);
      bubble.current = session.bubble;
      setBubbleId(session.bubble?.dbId)
      setAppState(States.Authenticated);
    } catch (err: any) {
      if (err.data?.message === "noAgentAvailable") {
        console.log("[App] No agent available : ", err.data.message);
        setAppState(States.Unavailability);
      } else if (err.data?.message === "badToken") {
        setAppState(States.BadToken);
      } else {
        console.log("[App] Error during initiateChatSession phase: ", err);
        setAppState(States.Error);
      }
    }
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", sessionVisibilityChange);

    const start = async () => {
      try {
        if (bubble && bubble.current && Object.keys(bubble.current).length !== 0) {
          const session = await UniversalLivechatService.getSession(bubble.current);
          setReasonForChat(
              Config.chatService.reasonForChats.find((r: ReasonForChat) => r.name === session.reasonForChat) || null
          );
          await RainbowSDKService.loginWithToken(session.token);
          setAppState(States.Authenticated);
        } else {
          console.log("[App] Room cookies not found - Start a new session");
          const firstReason = Config.chatService.reasonForChats[0];
          setReasonForChat(firstReason);
          initiateChatSession(I18nService.getUsedLanguage(), firstReason);
        }
      } catch (err: any) {
        cookies.remove("livechat_bubble");
        if (err.data && ["loginFailed", "sessionFailed"].includes(err.data.message)) {
          console.log("[App] Error While loading : ", err.data.message);

          // @ts-ignore
          const bubbleIdToDelete = bubbleId || bubble.id
          if (bubbleIdToDelete) {
            await UniversalLivechatService.deleteSession(bubbleIdToDelete, err.data);
            console.log("[App] session deleted - Starting a new session");
          }

          const firstReason = Config.chatService.reasonForChats[0];
          setReasonForChat(firstReason);
          setBubbleId("");
          // @ts-ignore
            bubble.current = {}
          initiateChatSession(I18nService.getUsedLanguage(), firstReason);
        } else {
          console.log("[App] Unhandled error :", err);
          setAppState(States.Error);
        }
      }
    };

    start();
  }, []);

  return (
      <div className="App">
        {(() => {
          if (appState === States.Initialising) {
            return <LoadingScreen message={t("loading_screen_livechat_loading")} />;
          } else if (appState === States.Initialised) {
            return <WelcomeChat appState={appState} setAppState={setAppState} States={States} handleReasonForChatChange={()=>""}/>;
          } else {
            return (
                <WelcomeChat
                    bubble={bubble.current}
                    appState={appState}
                    handleReasonForChatChange={handleReasonForChatChange}
                    reasonForChat={reasonForChat}
                    setAppState={setAppState}
                    reloadReasonForChat={() => {}}
                    States={States}
                    disabled={true}
                />
            );
          }
        })()}
      </div>
  );
};

export default withCookies(App);